import React from 'react';
import { Button, ConfigProvider, Skeleton, Typography } from 'antd';
import { ScopeLevel } from '../../types/returnInspection';
import { LEVEL_COLOR } from './ruleDisplay';

const { Text } = Typography;

/** 每一级标题下方的说明：这一级规则适用的范围 + 与其它级别的优先级关系（具体规则见页面后续的完整说明弹窗） */
export const LEVEL_DESCRIPTION = {
  customer: '适用于所有项目的所有库存单元，优先级最低。当未设置项目级规则或库存单元级规则时，必须设置通用规则，否则无法创建退货预报。',
  project: '仅适用于某个项目的所有库存单元，优先级高于通用规则，低于库存单元级规则。',
  inventory_unit: '仅适用于某个项目的特定库存单元，优先级最高。可设置"按流程初检"，进行外包装和内件检查后分流处理。',
} as const;

interface RuleLevelCardProps {
  level: ScopeLevel;
  title: string;
  /** 标题紧靠右侧的附加文字，如"当前项目: P2610 - 华东仓项目" */
  titleExtra?: React.ReactNode;
  /** 标题下方的一句说明：这一级规则的作用，以及没设置时用哪一级 */
  description: string;
  /** 这一级暂时无法使用（如管理员还没选客户），整张卡片置灰并显示这段说明 */
  placeholder?: string;
  /** 点击后打开编辑；不传表示只读（如子账号看通用规则） */
  onEdit?: () => void;
  /** 覆盖主按钮文案；默认按"本级有没有规则"显示 编辑/设置 */
  editLabel?: string;
  /** 覆盖主按钮是否为实心主按钮（彩底白字）；默认"本级还没有规则"时为实心 */
  editPrimary?: boolean;
  /** 移除本级规则（移除后自动回退到上级规则）；只在这一级已有规则、且当前用户有权限时才传 */
  onRemove?: () => void;
  removeLabel?: string;
  hasRule: boolean;
  loading?: boolean;
  /**
   * 展开内容：显示在状态行下方、用一条分隔线隔开，仍然在这张卡片的边框之内。
   * 库存单元级规则卡片用它在卡片内原地展开库存单元列表，整个板块只有一圈边框。
   * 传入时卡片统一用白底（灰底衬着表格不好看）。
   */
  expandedContent?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * 一个层级的规则卡片：标题 + 说明 + "本级已设置规则:" 状态。三个层级共用这一个组件，
 * 大小、宽度、版式完全一致，只有颜色不同（通用=蓝、项目级=橘、库存单元级=绿），
 * 边框、标题、按钮同一色系。
 */
const RuleLevelCard: React.FC<RuleLevelCardProps> = ({
  level,
  title,
  titleExtra,
  description,
  placeholder,
  onEdit,
  editLabel,
  editPrimary,
  onRemove,
  removeLabel = '移除规则',
  hasRule,
  loading,
  expandedContent,
  children,
}) => {
  const color = LEVEL_COLOR[level];
  // 没有规则的卡片用浅灰底表示"空"；展开了内容（如库存单元列表）就改回白底
  const muted = !!placeholder || (!hasRule && !expandedContent);
  return (
    // 用 ConfigProvider 把这张卡片里的主色换成该级颜色，按钮的悬停态也会跟着变，不会冒出默认的蓝色。
    // 暂不可用（占位）的卡片统一用灰色，表示这一级现在还用不了。
    <ConfigProvider theme={{ token: { colorPrimary: color } }}>
      <div
        style={{
          width: '100%',
          padding: '10px 14px 12px',
          borderRadius: 6,
          background: muted ? '#fafafa' : '#fff',
          border: `1px solid ${placeholder ? '#d9d9d9' : color}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {/* 标题 + 紧靠右侧的附加文字（如所属项目），一起靠左；按钮组靠右 */}
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <span style={{ fontWeight: 600, color: placeholder ? '#bfbfbf' : color }}>{title}</span>
            {titleExtra && <span style={{ fontSize: 13, color: '#595959' }}>{titleExtra}</span>}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* 加载中不显示按钮：此时规则还没取回来，会误显示成"设置"（实际已有规则应是"编辑"） */}
            {onEdit && !placeholder && !loading && (
              <Button size="small" type={(editPrimary ?? !hasRule) ? 'primary' : 'default'} onClick={onEdit}>
                {editLabel ?? (hasRule ? '编辑规则' : '设置规则')}
              </Button>
            )}
            {onRemove && hasRule && !placeholder && !loading && (
              <Button size="small" type="link" danger onClick={onRemove}>
                {removeLabel}
              </Button>
            )}
          </span>
        </div>
        {/* 这一级规则的作用 + 没设置时用哪一级；不随加载/占位状态变化 */}
        <div style={{ marginTop: 6, fontSize: 12, lineHeight: '18px', color: '#8c8c8c' }}>{description}</div>
        <div style={{ marginTop: 8, minHeight: 44 }}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 1 }} title={false} />
          ) : placeholder ? (
            <Text type="secondary">{placeholder}</Text>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>本级已设置规则:</div>
              {children}
            </>
          )}
        </div>
        {expandedContent && !placeholder && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>{expandedContent}</div>
        )}
      </div>
    </ConfigProvider>
  );
};

/**
 * 两张卡片之间的"过渡行"：一句说明，控件（选择项目下拉 / 展开·收起库存单元级规则设置）紧靠在说明文字的右侧，
 * 不推到整行最右边。
 */
export const ConnectorRow: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, flexWrap: 'wrap', padding: '0 4px', marginTop: 30 }}>
    <span style={{ color: '#595959', fontSize: 13 }}>{text}</span>
    <span>{children}</span>
  </div>
);

export default RuleLevelCard;
